import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  Button,
  Alert,
  Spinner,
  Input,
  FormGroup,
  Label,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAppSettings,
  updateAppSettings,
  clearMessages,
} from "../../redux/setting/appSettingsSlice";

const AppSettings = () => {
  const dispatch = useDispatch();
  const { android, ios, loading, error, success } = useSelector(
    (state) => state.appSettings
  );

  const [androidSettings, setAndroidSettings] = useState(android);
  const [iosSettings, setIosSettings] = useState(ios);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchAppSettings());
  }, [dispatch]);

  useEffect(() => {
    setAndroidSettings(android);
    setIosSettings(ios);
  }, [android, ios]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(clearMessages());
    }, 5000);

    return () => clearTimeout(timer);
  }, [error, success, dispatch]);

  const handleSave = async (device, settings) => {
    setSaving(true);
    try {
      await dispatch(updateAppSettings(device, settings));
      // Refresh data after successful update
      dispatch(fetchAppSettings());
    } catch (err) {
      console.error("Error saving settings:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (device, field, value) => {
    const settings = device === "android" ? androidSettings : iosSettings;
    const setSettings =
      device === "android" ? setAndroidSettings : setIosSettings;

    setSettings({
      ...settings,
      [field]: value,
    });
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner color="primary" />
        <p className="mt-2">Loading app settings...</p>
      </div>
    );
  }

  return (
    <Container className="mt-4">
      <h2 className="mb-4">App Settings</h2>

      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">{success}</Alert>}

      <Row>
        {/* Android Settings */}
        <Col md={6} className="mb-4">
          <Card>
            <CardHeader>
              <h5 className="mb-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  class="bi bi-android2"
                  viewBox="0 0 17 17"
                  className="me-2"
                >
                  <path d="m10.213 1.471.691-1.26q.069-.124-.048-.192-.128-.057-.195.058l-.7 1.27A4.8 4.8 0 0 0 8.005.941q-1.032 0-1.956.404l-.7-1.27Q5.281-.037 5.154.02q-.117.069-.049.193l.691 1.259a4.25 4.25 0 0 0-1.673 1.476A3.7 3.7 0 0 0 3.5 5.02h9q0-1.125-.623-2.072a4.27 4.27 0 0 0-1.664-1.476ZM6.22 3.303a.37.37 0 0 1-.267.11.35.35 0 0 1-.263-.11.37.37 0 0 1-.107-.264.37.37 0 0 1 .107-.265.35.35 0 0 1 .263-.11q.155 0 .267.11a.36.36 0 0 1 .112.265.36.36 0 0 1-.112.264m4.101 0a.35.35 0 0 1-.262.11.37.37 0 0 1-.268-.11.36.36 0 0 1-.112-.264q0-.154.112-.265a.37.37 0 0 1 .268-.11q.155 0 .262.11a.37.37 0 0 1 .107.265q0 .153-.107.264M3.5 11.77q0 .441.311.75.311.306.76.307h.758l.01 2.182q0 .414.292.703a.96.96 0 0 0 .7.288.97.97 0 0 0 .71-.288.95.95 0 0 0 .292-.703v-2.182h1.343v2.182q0 .414.292.703a.97.97 0 0 0 .71.288.97.97 0 0 0 .71-.288.95.95 0 0 0 .292-.703v-2.182h.76q.436 0 .749-.308.31-.307.311-.75V5.365h-9zm10.495-6.587a.98.98 0 0 0-.702.278.9.9 0 0 0-.293.685v4.063q0 .406.293.69a.97.97 0 0 0 .702.284q.42 0 .712-.284a.92.92 0 0 0 .293-.69V6.146a.9.9 0 0 0-.293-.685 1 1 0 0 0-.712-.278m-12.702.283a1 1 0 0 1 .712-.283q.41 0 .702.283a.9.9 0 0 1 .293.68v4.063a.93.93 0 0 1-.288.69.97.97 0 0 1-.707.284 1 1 0 0 1-.712-.284.92.92 0 0 1-.293-.69V6.146q0-.396.293-.68" />
                </svg>
                Android Version
              </h5>
            </CardHeader>
            <CardBody>
              <Form>
                <FormGroup>
                  <Label for="androidVersion">Version</Label>
                  <Input
                    type="text"
                    id="androidVersion"
                    value={androidSettings.version}
                    onChange={(e) =>
                      handleInputChange("android", "version", e.target.value)
                    }
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="androidTitle">Title</Label>
                  <Input
                    type="text"
                    id="androidTitle"
                    value={androidSettings.title}
                    onChange={(e) =>
                      handleInputChange("android", "title", e.target.value)
                    }
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="androidMessage">Message</Label>
                  <Input
                    type="textarea"
                    id="androidMessage"
                    rows="3"
                    value={androidSettings.message}
                    onChange={(e) =>
                      handleInputChange("android", "message", e.target.value)
                    }
                  />
                </FormGroup>

                <FormGroup check>
                  <Label check>
                    <Input
                      type="switch"
                      id="androidForceUpdate"
                      checked={androidSettings.forceUpdate}
                      onChange={(e) =>
                        handleInputChange(
                          "android",
                          "forceUpdate",
                          e.target.checked
                        )
                      }
                    />
                    Force Update
                  </Label>
                </FormGroup>

                <Button
                  color="primary"
                  onClick={() => handleSave("android", androidSettings)}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    "Save Android Settings"
                  )}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>

        {/* iOS Settings */}
        <Col md={6} className="mb-4">
          <Card>
            <CardHeader>
              <h5 className="mb-0">
                <i className="bi bi-apple me-2"></i>
                iOS Version
              </h5>
            </CardHeader>
            <CardBody>
              <Form>
                <FormGroup>
                  <Label for="iosVersion">Version</Label>
                  <Input
                    type="text"
                    id="iosVersion"
                    value={iosSettings.version}
                    onChange={(e) =>
                      handleInputChange("ios", "version", e.target.value)
                    }
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="iosTitle">Title</Label>
                  <Input
                    type="text"
                    id="iosTitle"
                    value={iosSettings.title}
                    onChange={(e) =>
                      handleInputChange("ios", "title", e.target.value)
                    }
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="iosMessage">Message</Label>
                  <Input
                    type="textarea"
                    id="iosMessage"
                    rows="3"
                    value={iosSettings.message}
                    onChange={(e) =>
                      handleInputChange("ios", "message", e.target.value)
                    }
                  />
                </FormGroup>

                <FormGroup check>
                  <Label check>
                    <Input
                      type="switch"
                      id="iosForceUpdate"
                      checked={iosSettings.forceUpdate}
                      onChange={(e) =>
                        handleInputChange(
                          "ios",
                          "forceUpdate",
                          e.target.checked
                        )
                      }
                    />
                    Force Update
                  </Label>
                </FormGroup>

                <Button
                  color="primary"
                  onClick={() => handleSave("ios", iosSettings)}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    "Save iOS Settings"
                  )}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AppSettings;
